/**
 * useHeaderTooltip · React hook for the Search.Inc header 'Actually not evil' hover tooltip.
 *
 * Owns the show/hide state for the evil-tooltip plus the timer ref that keeps
 * the tooltip alive for five seconds after the user's mouse leaves the logo
 * or the tooltip body. Every handler that used to sit inline inside the
 * KnowledgeGraphPage.jsx header JSX now lives here, so the header component
 * can stay declarative.
 *
 * Extracted 2026-08-01 · IJ-243 · Phase 5B of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md Section Three Boundary One).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor Programme 2026.
 * Owner: E1.
 *
 * Usage:
 *   const {
 *     showEvilTooltip,
 *     onLogoMouseEnter,
 *     onLogoMouseLeave,
 *     onTooltipMouseEnter,
 *     onTooltipMouseLeave,
 *     hideEvilTooltip,
 *   } = useHeaderTooltip(isMobile);
 *
 * Behaviour parity with the original inline handlers (KnowledgeGraphPage.jsx
 * lines 2891-2924 before extraction):
 *   - onLogoMouseEnter: no-op on mobile; on desktop clears any pending hide
 *     timer and shows the tooltip immediately.
 *   - onLogoMouseLeave: schedules a hide 5000 ms later.
 *   - onTooltipMouseEnter: cancels the pending hide timer so the user can move
 *     the mouse onto the tooltip body without it vanishing.
 *   - onTooltipMouseLeave: reschedules the 5000 ms hide.
 *   - hideEvilTooltip: force-hide (used by the 'Don't Show' button and by the
 *     'Compare us vs the rest' click that opens the menu overlay).
 */
import { useState, useRef, useCallback } from 'react';

export const useHeaderTooltip = (isMobile) => {
  const [showEvilTooltip, setShowEvilTooltip] = useState(false);
  const evilTooltipTimeoutRef = useRef(null);

  const onLogoMouseEnter = useCallback(() => {
    if (isMobile) return;
    if (evilTooltipTimeoutRef.current) {
      clearTimeout(evilTooltipTimeoutRef.current);
    }
    setShowEvilTooltip(true);
  }, [isMobile]);

  const onLogoMouseLeave = useCallback(() => {
    evilTooltipTimeoutRef.current = setTimeout(() => setShowEvilTooltip(false), 5000);
  }, []);

  const onTooltipMouseEnter = useCallback(() => {
    if (evilTooltipTimeoutRef.current) {
      clearTimeout(evilTooltipTimeoutRef.current);
    }
  }, []);

  const onTooltipMouseLeave = useCallback(() => {
    evilTooltipTimeoutRef.current = setTimeout(() => setShowEvilTooltip(false), 5000);
  }, []);

  const hideEvilTooltip = useCallback(() => {
    setShowEvilTooltip(false);
  }, []);

  return {
    showEvilTooltip,
    onLogoMouseEnter,
    onLogoMouseLeave,
    onTooltipMouseEnter,
    onTooltipMouseLeave,
    hideEvilTooltip,
  };
};

export default useHeaderTooltip;
